/**
 * One editor per project across tabs and windows (Web Locks API).
 *
 * The project would otherwise be saved as a whole document by every tab that has it
 * open, and the last tab to save would silently overwrite the others' edits. An open
 * project holds an exclusive lock named after its id; the browser releases it when the
 * tab closes or crashes.
 *
 * Outcomes are explicit, and only two let a project open:
 * - `acquired`: this tab holds the lock until `release()` (after the final save);
 * - `unsupported`: the API does not exist in this browser. The documented fallback opens
 *   the project with no cross-tab protection, and the UI says so.
 * The other two never open it:
 * - `held-elsewhere`: another tab is editing it;
 * - `failed`: the API exists but the request failed. It is never treated as "free" (that
 *   would silently drop the protection); the user can retry.
 */
export type LockOutcome =
  | { kind: 'acquired'; release: () => void }
  | { kind: 'held-elsewhere' }
  | { kind: 'failed'; error: unknown }
  | { kind: 'unsupported' };

/** `onLost` runs if a lock that was acquired is later taken away (e.g. stolen by another context). */
export type ProjectLock = (projectId: string, onLost?: (error: unknown) => void) => Promise<LockOutcome>;

interface LockManagerLike {
  request(name: string, options: { ifAvailable: boolean }, callback: (lock: unknown) => Promise<void> | undefined): Promise<unknown>;
}

const lockManager = (): LockManagerLike | undefined => {
  const manager = (globalThis.navigator as (Navigator & { locks?: LockManagerLike }) | undefined)?.locks;
  return typeof manager?.request === 'function' ? manager : undefined;
};

export const projectLockName = (projectId: string) => `sonicforge-project:${projectId}`;

/** True when this browser has the Web Locks API (cross-tab protection is possible). */
export const webLocksSupported = () => lockManager() !== undefined;

export const acquireProjectLock: ProjectLock = (projectId, onLost) => {
  const manager = lockManager();
  if (!manager) return Promise.resolve({ kind: 'unsupported' });
  return new Promise<LockOutcome>((resolve) => {
    let settled = false;
    const settle = (outcome: LockOutcome) => {
      settled = true;
      resolve(outcome);
    };
    let request: Promise<unknown>;
    try {
      request = manager.request(projectLockName(projectId), { ifAvailable: true }, (lock) => {
        if (!lock) {
          settle({ kind: 'held-elsewhere' });
          return undefined;
        }
        // Held until release() resolves this promise.
        return new Promise<void>((release) => settle({ kind: 'acquired', release: () => release() }));
      });
    } catch (error) {
      settle({ kind: 'failed', error });
      return;
    }
    request.catch((error: unknown) => {
      // Before the callback ran: the request itself failed. After it: the lock was lost.
      if (!settled) settle({ kind: 'failed', error });
      else onLost?.(error);
    });
  });
};

export type LockedResult<T> = { kind: 'done'; value: T } | { kind: 'unprotected'; value: T } | { kind: 'held-elsewhere' } | { kind: 'failed'; error: unknown };

/**
 * Runs `action` while holding the project's lock (Dashboard rename and delete), so no tab
 * can open the project in between. Refuses when another tab holds the lock and when the
 * lock state cannot be determined; a failed check never counts as "not open elsewhere".
 * Without the API, the action runs unprotected (documented) and the result says so.
 */
export async function withProjectLock<T>(projectId: string, action: () => Promise<T>): Promise<LockedResult<T>> {
  const outcome = await acquireProjectLock(projectId);
  if (outcome.kind === 'unsupported') return { kind: 'unprotected', value: await action() };
  if (outcome.kind !== 'acquired') return outcome;
  try {
    return { kind: 'done', value: await action() };
  } finally {
    outcome.release();
  }
}
